import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, CardContent, Image, Paragraph, Progress } from '@app/shared/ui/atoms/Common';
import { TASK_ICONS } from '@app/shared/constants/tasks';
import { TaskModel, TaskStatus } from '@app/store/slices/tasks/taskTypes';
import { checkIcon, cookieImage } from '@app/assets';

interface TaskCardProps {
  task: TaskModel;
  onClick: (task: TaskModel, event: React.MouseEvent<HTMLElement>) => void;
  isBtnDisabled: boolean;
}

export const TaskCard = memo(function TaskCard({ task, onClick, isBtnDisabled }: TaskCardProps) {
  const { t } = useTranslation();

  const Icon = TASK_ICONS[task.key];

  return (
    <Card className="rounded-2xl border-none bg-blue-soft px-4 py-2.5">
      <CardContent className="grid grid-cols-[1fr_102px] items-center gap-2 p-0">
        <div className="flex flex-2 flex-col gap-1">
          <div className="flex items-center space-x-2">
            <Icon className="min-h-6 min-w-6 text-accent" />
            <Paragraph variant="accent" size="sm" className="line-clamp-2 max-w-52">
              {task.description}
            </Paragraph>
          </div>
          <div className="grid grid-cols-[1fr_40px] items-center gap-2">
            <Progress
              value={(task.progress.value / task.progress.total) * 100}
              className="bg-gray-200"
            />
            <Paragraph
              variant="accent"
              className="text-nowrap"
              size="sm"
            >{`${task.progress.value}/${task.progress.total}`}</Paragraph>
          </div>
        </div>

        <div className="flex flex-1 flex-col items-end space-y-2">
          {task.status === TaskStatus.COMPLETED && (
            <img src={checkIcon} alt="checkmark icon" className="h-6 w-6" />
          )}
          {(task.status === TaskStatus.READY || task.status === TaskStatus.IN_PROGRESS) && (
            <>
              <div className="flex w-full items-center justify-center gap-1">
                <Paragraph size="sm" variant="accent">
                  {!!task.xpReward && task.xpReward + ' XP'}
                  {' / '}
                  {!!task.cookieReward && task.cookieReward}
                </Paragraph>
                <Image src={cookieImage} alt="cookie image" className="w-4 min-w-4" />
              </div>
              <Button
                size="sm"
                className="rounded-full px-[6px] text-xs"
                onClick={(event) => onClick(task, event)}
                disabled={isBtnDisabled}
              >
                {task.status === TaskStatus.IN_PROGRESS ? t('common.get') : t('tasks.receive')}{' '}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
});
